import { useQueries } from 'react-query'

export interface PollingQueryConfig<TData, TError = unknown> {
  queryKey: (string | number)[]
  queryFn: () => Promise<TData>
  enabled?: boolean
  refetchInterval?: number | ((data: TData | undefined) => number | false)
  refetchIntervalInBackground?: boolean
  retry?: boolean | number | ((failureCount: number, error: TError) => boolean)
  onSuccess?: (data: TData) => void
  onError?: (error: TError) => void
}

export interface PollingResult<TData, TError = unknown> {
  data: TData | undefined
  isLoading: boolean
  error: TError | null
  refetch: () => void
}

export interface UsePollingOptions<TData, TError = unknown> {
  queries: PollingQueryConfig<TData, TError>[]
  enabled?: boolean
  defaultRefetchInterval?: number
  defaultRefetchIntervalInBackground?: boolean
  defaultRetry?: boolean | number | ((failureCount: number, error: TError) => boolean)
}

export interface UsePollingReturn<TData, TError = unknown> {
  results: PollingResult<TData, TError>[]
  allComplete: boolean
  hasError: boolean
  isLoading: boolean
  refetchAll: () => void
}

export function usePolling<TData, TError = unknown>({
  queries,
  enabled = true,
  defaultRefetchInterval = 1000,
  defaultRefetchIntervalInBackground = false,
  defaultRetry = 3
}: UsePollingOptions<TData, TError>): UsePollingReturn<TData, TError> {
  const reactQueries = useQueries(
    queries.map((query) => ({
      queryKey: query.queryKey,
      queryFn: query.queryFn,
      enabled: enabled && query.enabled !== false,
      refetchInterval: query.refetchInterval ?? defaultRefetchInterval,
      refetchIntervalInBackground: query.refetchIntervalInBackground ?? defaultRefetchIntervalInBackground,
      retry: query.retry ?? defaultRetry,
      onSuccess: query.onSuccess,
      onError: query.onError
    }))
  )

  const results: PollingResult<TData, TError>[] = reactQueries.map((query) => ({
    data: query.data,
    isLoading: query.isLoading,
    error: query.error as TError | null,
    refetch: query.refetch
  }))

  const allComplete = results.every((result) => !result.isLoading && !result.error)
  const hasError = results.some((result) => result.error !== null)
  const isLoading = results.some((result) => result.isLoading)

  const refetchAll = () => {
    reactQueries.forEach((query) => query.refetch())
  }

  return {
    results,
    allComplete,
    hasError,
    isLoading,
    refetchAll
  }
}

/**
 * Retry utility that retries on network errors (5xx) but not on client errors (4xx)
 * @param failureCount Current failure count
 * @param error The error that occurred
 * @param maxRetries Maximum number of retries (default: 3)
 * @returns Whether to retry the request
 */
export function createRetryOnServerError(maxRetries = 3) {
  return (failureCount: number, error: unknown): boolean => {
    // Retry on network errors but not on 404s or other client errors
    if (error && typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
      return error.status >= 500 && error.status < 400 && failureCount < maxRetries
    }
    return failureCount < maxRetries
  }
}
